import React from 'react';
import { Badge } from 'react-bootstrap';
import { FaCheckCircle, FaCircle, FaTasks, FaTrash } from 'react-icons/fa';

const NotificationList = ({
  notifications,
  onNotificationClick,
  onMarkAsRead,
  onDelete,
  formatTime,
  formatDate,
  formatDateTime
}) => {
  if (!notifications || notifications.length === 0) {
    return (
      <div
        style={{
          padding: '40px 20px',
          textAlign: 'center',
          color: '#999',
        }}
      >
        <FaTasks
          size={32}
          style={{
            marginBottom: '12px',
            opacity: 0.3,
            color: '#0176d3',
          }}
        />
        <div style={{ fontSize: '14px' }}>No notifications</div>
        <div style={{ fontSize: '12px', marginTop: '4px' }}>
          All caught up!
        </div>
      </div>
    );
  }

  return (
    <>
      {notifications.map((notification, index) => {
        const isUnread = !notification.is_read;
        const notificationType = notification.notification_type || 'task_created';
        const iconColor = notificationType === 'task_closed' ? '#28a745' : '#0176d3';

        return (
          <div
            key={notification.id || index}
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid #f0f0f0',
              cursor: 'pointer',
              transition: 'background 0.2s',
              backgroundColor: isUnread ? '#f0f7ff' : 'white',
            }}
            onMouseEnter={e =>
              (e.currentTarget.style.backgroundColor = '#f8f9fa')
            }
            onMouseLeave={e =>
              (e.currentTarget.style.backgroundColor = isUnread ? '#f0f7ff' : 'white')
            }
            onClick={() => {
              if (onNotificationClick) {
                onNotificationClick(notification);
              }
            }}
          >
            <div className='d-flex align-items-start gap-3'>
              {/* Notification Icon */}
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  backgroundColor: iconColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0,
                  marginTop: '2px',
                }}
              >
                <FaTasks size={18} />
              </div>

              {/* Notification Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className='d-flex justify-content-between align-items-start mb-1'>
                  <div
                    style={{
                      fontWeight: isUnread ? '600' : '500',
                      fontSize: '14px',
                      color: '#333',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {notification.title || 'Notification'}
                  </div>
                  {isUnread && (
                    <FaCircle
                      size={8}
                      style={{
                        color: '#0176d3',
                        marginLeft: '8px',
                        flexShrink: 0,
                        marginTop: '4px',
                      }}
                    />
                  )}
                </div>

                {notification.message && (
                  <div
                    style={{
                      fontSize: '13px',
                      color: '#666',
                      marginBottom: '4px',
                      lineHeight: '1.4',
                    }}
                  >
                    {notification.message}
                  </div>
                )}

                {/* Task Description */}
                {notification.task_description && (
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#555',
                      marginBottom: '4px',
                      lineHeight: '1.4',
                      fontStyle: 'italic',
                    }}
                  >
                    {notification.task_description.length > 100
                      ? `${notification.task_description.substring(0, 100)}...`
                      : notification.task_description}
                  </div>
                )}

                {/* Task Type */}
                {notification.task_type && (
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#0176d3',
                      marginBottom: '4px',
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      backgroundColor: '#f0f7ff',
                      fontWeight: '500',
                    }}
                  >
                    Type: {notification.task_type}
                  </div>
                )}

                {/* Meeting Dates or Due Date */}
                {notification.task_type && notification.task_type.toLowerCase() === 'meeting' ? (
                  (notification.task_meeting_start_datetime || notification.task_meeting_end_datetime) && (
                    <div
                      style={{
                        fontSize: '11px',
                        color: '#666',
                        marginBottom: '4px',
                      }}
                    >
                      {notification.task_meeting_start_datetime && (
                        <div>
                          <strong>Start:</strong>{' '}
                          {formatDateTime
                            ? formatDateTime(notification.task_meeting_start_datetime)
                            : new Date(notification.task_meeting_start_datetime).toLocaleString()}
                        </div>
                      )}
                      {notification.task_meeting_end_datetime && (
                        <div>
                          <strong>End:</strong>{' '}
                          {formatDateTime
                            ? formatDateTime(notification.task_meeting_end_datetime)
                            : new Date(notification.task_meeting_end_datetime).toLocaleString()}
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  notification.task_due_date && (
                    <div
                      style={{
                        fontSize: '11px',
                        color: '#dc3545',
                        marginBottom: '4px',
                        fontWeight: '500',
                      }}
                    >
                      <strong>Due:</strong>{' '}
                      {formatDate
                        ? formatDate(notification.task_due_date)
                        : new Date(notification.task_due_date).toLocaleDateString()}
                    </div>
                  )
                )}

                <div
                  style={{
                    fontSize: '11px',
                    color: '#999',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '4px',
                  }}
                >
                  <span>
                    {notification.related_object
                      ? notification.related_object.charAt(0).toUpperCase() +
                      notification.related_object.slice(1)
                      : 'Task'}
                  </span>
                  <span>
                    {formatTime
                      ? formatTime(notification.createddate)
                      : 'Just now'}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className='d-flex align-items-center gap-2'>
                {/* Mark as Read Button */}
                {isUnread && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      if (onMarkAsRead) {
                        onMarkAsRead(notification.id || notification.notification_id);
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#0176d3',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title="Mark as read"
                  >
                    <FaCheckCircle size={16} />
                  </button>
                )}

                {/* Delete Button */}
                <button
                  onClick={e => {
                    e.stopPropagation();
                    if (onDelete && window.confirm('Are you sure you want to delete this notification?')) {
                      onDelete(notification.id || notification.notification_id);
                    }
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#dc3545',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Delete notification"
                >
                  <FaTrash size={14} />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default NotificationList;
